import dateToTimeString from 'components/calendar/components'

import { isBefore } from 'date-fns'
import dayjs, { Dayjs } from 'dayjs'
import { t } from 'i18next'
import { getDefaultTimezone } from 'index'
import database from 'model'
import { updateEventOnServer } from 'services/api/event'
import Database from 'services/db/Database'
import {
  excludeInstance,
  localEventById,
  localEventByLocalId,
  RemoveTempEvents
} from 'services/db/Queries/Event'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import {
  AggregatedDeliveryStatus,
  EventDoc,
  EventStatus,
  LastMessage,
  ParticipantDataDoc
} from 'types/api/changes'
import { CollectionType, Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import { is24meApp, isGroupcalApp } from 'utils/appType'
import { groupIdToProvider, ProviderType } from 'utils/groups'

import tick1 from '../../assets/icons/event-status/1tick.svg'
import tick2 from '../../assets/icons/event-status/2tick.svg'
import tick2Completed from '../../assets/icons/event-status/2tick_comp.svg'
import notsent from '../../assets/icons/event-status/not-sent.svg'
import { generateGroupcalArrayFromRecurrence } from 'utils/recurrence'
import { isLateInstance, isSomeDayInstance } from './instance_state'
import {
  generateLateRecurrentInstances,
  generateLateTasksForEvents,
  prepareTasksForUI
} from 'utils/fc_mapping'
import { isTrulyEmpty } from 'utils/object'
import { bookedEvent } from 'utils/event'

function formatEventLastUpdate(event: EventDoc): string {
  if (!event.StartDate || event.StartDate === 'null') return ''

  return dayjs(Number(event.StartDate) * 1000).format(
    event.AllDay === '1' ? 'L' : 'L LT'
  )
}

export function eventStatusIcon(event?: EventDoc): string {
  if (!event) return notsent as unknown as string

  const {
    AggregatedParticipantsDeliveryStatus,
    AggregatedParticipantsConfirmationStatus
  } = event

  if (event._id?.length === 0) return notsent as unknown as string
  if (
    AggregatedParticipantsConfirmationStatus != null &&
    AggregatedParticipantsConfirmationStatus.length > 0
  )
    return tick2Completed as unknown as string

  if (AggregatedParticipantsDeliveryStatus != null) {
    if (
      Number(AggregatedParticipantsDeliveryStatus) >=
      Number(AggregatedDeliveryStatus.DELIVERED)
    ) {
      return tick2 as unknown as string
    } else {
      return tick1 as unknown as string
    }
  }

  return notsent as unknown as string
}

function isEventNotAdded(event: EventDoc): boolean {
  return `${event?.Status}` === EventStatus.HARD_DELETE_FROM_DB
}

function isEventActive(event: EventDoc): boolean {
  return `${event?.Status}` === EventStatus.ACTIVE
}

export function addEventMessage(
  event: EventDoc,
  message?: LastMessage
): EventDoc {
  if (message && message.title === bookedEvent) {
    message.title = t('bookedSlot')
  }
  return {
    ...event,
    StartDate: String(event.StartDate),
    EndDate: String(event.EndDate),
    LastUpdateMessage: message
  }
}

export function buildEditEventMessage(
  userID: string,
  newEvent: EventDoc,
  prevEvent: EventDoc,
  participants: Model<ParticipantDataDoc>[]
): EventDoc {
  const isCurrentUserChangedEvent = newEvent.UserID === userID
  const lastUpdatedDate = formatEventLastUpdate(newEvent)
  const eventDetails = `${lastUpdatedDate} ${newEvent.Text}`
  const userMadeAction = participants.find(
    (participant) => participant.AccountID === newEvent.UserID
  )

  let userTitleMadeChange =
    userMadeAction?.FullName ?? userMadeAction?.PhoneNumber ?? ''

  if (userTitleMadeChange === 'null') {
    userTitleMadeChange = ''
  }

  if (isCurrentUserChangedEvent) {
    userTitleMadeChange = t('you')
  }

  const eventStart = dayjs(Number(newEvent.StartDate) * 1000)
  const isAllDay = newEvent.AllDay === '1'
  const whenDate = isAllDay
    ? eventStart.format('MMM DD YYYY')
    : `${eventStart.format('MMM DD YYYY')}, ${dateToTimeString(
        eventStart.toDate(),
        true
      )}`

  if (!prevEvent) {
    if (newEvent.OwnerID === userID) {
      return addEventMessage(newEvent, {
        title: newEvent.Text,
        type: `${t('eventAdded')}`,
        who: `${t('you')}`,
        when: whenDate
      })
    } else {
      return addEventMessage(newEvent, {
        title: newEvent.Text,
        type: `${t('eventAdded')}`,
        who: userTitleMadeChange,
        when: whenDate
      })
    }
  }

  if (
    prevEvent.LastUpdate !== newEvent.LastUpdate ||
    prevEvent.ThirdPartyLastUpdate !== newEvent.ThirdPartyLastUpdate
  ) {
    if (isEventNotAdded(prevEvent) && isEventNotAdded(newEvent)) {
      return addEventMessage(newEvent, {
        title: newEvent.Text,
        type: 'event not added, error',
        who: userTitleMadeChange,
        when: whenDate
      })
    } else if (
      (isEventActive(prevEvent) && !isEventActive(newEvent)) ||
      (!isEventActive(prevEvent) && !isEventActive(newEvent))
    ) {
      if (isCurrentUserChangedEvent) {
        return addEventMessage(newEvent, {
          title: newEvent.Text,
          type: `${t('eventDeleted')}`,
          who: userTitleMadeChange,
          when: whenDate
        })
      } else {
        return addEventMessage(newEvent, {
          title: newEvent.Text,
          type: `${t('eventDeleted')}`,
          who: userTitleMadeChange,
          when: whenDate
        })
      }
    } else {
      return addEventMessage(newEvent, {
        title: newEvent.Text,
        type: `${t('eventUpdated')}`,
        who: userTitleMadeChange,
        when: whenDate
      })
    }
  }

  return addEventMessage(newEvent, prevEvent.LastUpdateMessage ?? { type: '' })
}

export function buildAddEventMessage(userID: string, newEvent: EventDoc) {
  const eventStart = dayjs(Number(newEvent.StartDate) * 1000)
  const isAllDay = newEvent.AllDay === '1'
  const whenDate = isAllDay
    ? eventStart.format('MMM DD YYYY')
    : `${eventStart.format('MMM DD YYYY')}, ${dateToTimeString(
        eventStart.toDate(),
        true
      )}`

  if (newEvent.OwnerID === userID) {
    return addEventMessage(
      { ...newEvent, Status: EventStatus.ACTIVE },
      {
        title: newEvent.Text,
        type: `${t('eventAdded')}`,
        who: `${t('you')}`,
        when: whenDate
      }
    )
  } else {
    return addEventMessage(
      { ...newEvent, Status: EventStatus.ACTIVE },
      {
        title: newEvent.Text,
        type: `${t('eventAdded')}`,
        when: whenDate
      }
    )
  }
}

interface GroupStatus {
  outOfTierGroup: boolean
  canEdit: boolean
}

export async function handleEventUpdate(
  event: Model<EventDoc>,
  data: EventDoc,
  shouldSaveLocally: boolean,
  isDirty: boolean
): Promise<void> {
  /**
   * This field determines, if an event should be updated locally only, without touching the server
   * For example, we save reminders only locally
   */

  Logger.blue('EVENT_UPDATE', event, data, shouldSaveLocally, isDirty)
  const assignFields =
    (ignoreRecurrence: boolean, data: EventDoc) =>
    (localEvent: Model<EventDoc>) => {
      const now = Date.now().valueOf() / 1000
      localEvent.LastUpdate = String(now)
      localEvent.RequestConfirmation = data.RSVP

      //in case of ignore recurrence, we remove it from data object
      if (ignoreRecurrence) {
        delete data['Recurrence']
      }

      Object.keys(data).forEach((key) => {
        if (key !== 'id') localEvent[key] = data[key]
      })
    }

  const provider = groupIdToProvider(data.GroupID ?? '')

  return localEventByLocalId(event.id).then(async (localEvent) => {
    if (shouldSaveLocally) {
      await database.write(async () => {
        await localEvent.update(assignFields(false, data))
      })
    } else {
      if (
        isDirty ||
        data.Status === EventStatus.DELETED ||
        data.attendees
          ?.find((attendee) => attendee.self)
          ?.responseStatus?.toLowerCase() === 'declined'
      ) {
        return await database
          .write(async () => {
            if (
              provider !== ProviderType.GOOGLE &&
              provider !== ProviderType.OUTLOOK
            ) {
              await localEvent.update(assignFields(false, data))
            } else {
              await Promise.resolve()
            }
          })
          .then(() => {
            return updateEventOnServer(event, data)
          })
          .then(() => {
            if (
              data.attendees
                ?.find((attendee) => attendee.self)
                ?.responseStatus?.toLowerCase() === 'declined'
            ) {
              return Database.write(async () => {
                return event.destroyPermanently()
              })
            }
          })
      }
    }
  })
}

export async function excludeInstanceFromRecurrence(
  data: EventDoc,
  event: Model<EventDoc> | EventDoc,
  instanceStart: number,
  instanceEnd: number
) {
  LocalStorage.remove(LocalStorageKey.VISIBLE_EVENT)
  const excluded = await excludeInstance(
    instanceStart,
    await localEventById(event._id ?? ''),
    isGroupcalApp()
      ? EventStatus.REMOVED
      : data.Status === EventStatus.ACTIVE
        ? EventStatus.DELETED
        : (data.Status ?? EventStatus.DELETED)
  )

  Logger.debug('excluded', excluded)

  await updateEventOnServer(excluded, {
    ...data,
    Recurrence: excluded.Recurrence,
    Status: EventStatus.ACTIVE, // to keep original series state
    StartDate: excluded.StartDate, // to keep original start series
    EndDate: excluded.EndDate, // to keep original start series
    Text: excluded.Text // to keep original start series
  })
}

export function isLate(event: EventDoc, ignoreRecurrence?: boolean) {
  return isLateInstance(event, ignoreRecurrence)
}

export function isSomeDay(event: EventDoc) {
  return isSomeDayInstance(event)
}

export function buildEventsArrayForUIFromDBEventsWithRecurrentEvents(
  events: EventDoc[],
  from: Dayjs,
  to: Dayjs
) {
  let itemsForUI: EventDoc[] = []

  prepareTasksForUI(
    events,
    LocalStorage.get(LocalStorageKey.SOMEDAY_DATE) ?? dayjs().unix().toString(),
    LocalStorage.get(LocalStorageKey.SOMEDAY_ALL_DAY) ??
      dayjs().unix().toString()
  ).forEach((event) => {
    const parsedItems = generateGroupcalArrayFromRecurrence(
      event,
      false,
      from,
      to,
      getDefaultTimezone()
    )

    itemsForUI.push(...parsedItems)
  })

  if (is24meApp()) {
    itemsForUI = [
      ...itemsForUI,
      ...generateLateTasksForEvents(
        itemsForUI.filter(
          (event) => isLate(event, true) && (event._id?.length ?? 0 > 0)
        )
      ),
      ...generateLateRecurrentInstances(events)
    ]
  }
  return itemsForUI
}

export const eventComparator = (o1: EventDoc, o2: EventDoc): number => {
  const event1AllDay = o1.AllDay === '1' || o1.late ? '1' : '0'
  const event2AllDay = o2.AllDay === '1' || o2.late ? '1' : '0'
  const status1 = isTrulyEmpty(o1.Status) ? EventStatus.ACTIVE : o1.Status
  const status2 = isTrulyEmpty(o2.Status) ? EventStatus.ACTIVE : o2.Status

  const statusState = Number(status1) - Number(status2)
  const allDayState = Number(event2AllDay) - Number(event1AllDay)
  const startDateState =
    Number(o1.lateOriginalStartTime ?? o1.StartDate ?? '0') -
    Number(o2.lateOriginalEndTime ?? o2.StartDate ?? '0')
  const rankState = Number(o1.Rank ?? '0') - Number(o2.Rank ?? '0')

  return (
    // Compare by status
    statusState ||
    // Compare by allDay
    allDayState ||
    // Compare by start time
    startDateState ||
    // Compare by rank
    rankState
  )
}

export function saveEventToLocalDB(event: EventDoc) {
  return RemoveTempEvents('After save').then(() => {
    return Database.write(async () => {
      return Database.getCollection<EventDoc>(CollectionType.EVENT).create(
        (localEvent) => {
          Object.keys(event).forEach((key) => {
            if (key !== 'id') localEvent[key] = event[key]
          })
        }
      )
    })
  })
}
